if (!customElements.get('ws-engraving')) {
  customElements.define(
    'ws-engraving',
    class WsEngraving extends HTMLElement {
      connectedCallback() {
        this.abortController?.abort();
        this.submitButtonObserver?.disconnect();
        this.quantityObserver?.disconnect();

        this.abortController = new AbortController();
        const { signal } = this.abortController;

        this.optionInputs = this.querySelectorAll('input[name="properties[Add engraving]"]');
        this.details = this.querySelector('.ws-product-engraving__details');
        this.textInput = this.querySelector('[data-ws-engraving-text]');
        this.locationInputs = this.querySelectorAll('input[name="properties[Engraving location]"]');
        this.counter = this.querySelector('[data-engraving-count]');
        this.errorMessage = this.querySelector('[data-ws-engraving-error]');
        this.form = this.closest('form');
        this.submitButton = this.form?.querySelector('button[type="submit"][name="add"]');
        this.acceleratedCheckout = this.form?.querySelector('[data-ws-accelerated-checkout]');
        this.variantInput = this.form?.elements.namedItem('id');
        this.quantityInput = this.form?.elements.namedItem('quantity');
        this.errorText = this.errorMessage?.textContent.trim() || 'Enter engraving text before continuing.';

        this.handleOptionChange = () => this.updateState();
        this.handleTextInput = () => this.updateState();
        this.handlePurchaseAttempt = (event) => this.validatePurchaseAttempt(event);
        this.handlePurchaseKeydown = (event) => {
          if (event.key === 'Enter' || event.key === ' ') this.validatePurchaseAttempt(event);
        };
        this.handleNativeStateChange = () => this.updatePurchaseState();

        this.optionInputs.forEach((input) => input.addEventListener('change', this.handleOptionChange, { signal }));
        this.textInput?.addEventListener('input', this.handleTextInput, { signal });
        this.form?.addEventListener('submit', this.handlePurchaseAttempt, { capture: true, signal });
        this.variantInput?.addEventListener('change', this.handleNativeStateChange, { signal });
        this.quantityInput?.addEventListener('input', this.handleNativeStateChange, { signal });
        this.quantityInput?.addEventListener('change', this.handleNativeStateChange, { signal });
        this.acceleratedCheckout?.addEventListener('click', this.handlePurchaseAttempt, { capture: true, signal });
        this.acceleratedCheckout?.addEventListener('keydown', this.handlePurchaseKeydown, { capture: true, signal });

        if (this.submitButton) {
          this.submitButtonObserver = new MutationObserver(() => this.updatePurchaseState());
          this.submitButtonObserver.observe(this.submitButton, {
            attributes: true,
            attributeFilter: ['disabled', 'aria-disabled'],
          });
        }

        if (this.quantityInput) {
          this.quantityObserver = new MutationObserver(() => this.updatePurchaseState());
          this.quantityObserver.observe(this.quantityInput, {
            attributes: true,
            attributeFilter: ['min', 'max', 'step', 'disabled'],
          });
        }

        this.updateState();
      }

      disconnectedCallback() {
        this.abortController?.abort();
        this.submitButtonObserver?.disconnect();
        this.quantityObserver?.disconnect();
      }

      updateState() {
        const isEnabled = this.engravingRequested;

        this.details.hidden = !isEnabled;
        if (this.textInput) {
          this.textInput.disabled = !isEnabled;
          this.textInput.required = isEnabled;
          this.textInput.setAttribute('aria-required', String(isEnabled));
          if (!isEnabled) this.textInput.value = '';
        }
        this.locationInputs.forEach((input) => {
          input.disabled = !isEnabled;
        });

        this.updateCounter();
        this.updatePurchaseState();
      }

      updateCounter() {
        if (this.counter) this.counter.textContent = this.textInput?.value.length || 0;
      }

      updatePurchaseState() {
        const engravingInvalid = !this.engravingValid;
        const purchaseValid = this.nativePurchaseValid && !engravingInvalid;

        this.dataset.purchaseValid = String(purchaseValid);
        this.form?.setAttribute('data-ws-purchase-valid', String(purchaseValid));

        if (this.textInput) {
          this.textInput.setCustomValidity(engravingInvalid ? this.errorText : '');
          this.textInput.setAttribute('aria-invalid', String(engravingInvalid));
        }

        if (this.errorMessage) this.errorMessage.hidden = !engravingInvalid;

        if (this.submitButton) {
          if (engravingInvalid) {
            if (this.submitButton.getAttribute('aria-disabled') !== 'true') {
              this.submitButton.dataset.wsEngravingDisabled = 'true';
              this.submitButton.setAttribute('aria-disabled', 'true');
            }
          } else if (this.submitButton.dataset.wsEngravingDisabled === 'true') {
            delete this.submitButton.dataset.wsEngravingDisabled;
            this.submitButton.removeAttribute('aria-disabled');
          }
        }

        if (this.acceleratedCheckout) {
          this.acceleratedCheckout.toggleAttribute('inert', !purchaseValid);
          this.acceleratedCheckout.setAttribute('aria-disabled', String(!purchaseValid));
          this.acceleratedCheckout.classList.toggle('is-disabled', !purchaseValid);
        }

        return purchaseValid;
      }

      validatePurchaseAttempt(event) {
        if (this.updatePurchaseState()) return true;

        event?.preventDefault();
        event?.stopImmediatePropagation();

        if (!this.engravingValid) {
          this.revealEngravingError();
        } else if (this.quantityInput && !this.quantityInput.checkValidity()) {
          this.quantityInput.reportValidity();
        }

        return false;
      }

      revealEngravingError() {
        if (this.errorMessage) this.errorMessage.hidden = false;
        this.textInput?.focus({ preventScroll: true });
        this.textInput?.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
      }

      get engravingRequested() {
        return (
          this.querySelector('input[name="properties[Add engraving]"]:checked')?.value === 'Add free engraving'
        );
      }

      get engravingValid() {
        return !this.engravingRequested || Boolean(this.textInput?.value.trim());
      }

      get nativePurchaseValid() {
        const variantValid = Boolean(this.variantInput?.value) && !this.variantInput?.disabled;
        const quantityValid = !this.quantityInput || this.quantityInput.checkValidity();
        const engravingOwnsAriaDisabled = this.submitButton?.dataset.wsEngravingDisabled === 'true';
        const ariaDisabledByNativeState =
          this.submitButton?.getAttribute('aria-disabled') === 'true' && !engravingOwnsAriaDisabled;
        const submitAvailable = Boolean(this.submitButton) && !this.submitButton.disabled && !ariaDisabledByNativeState;

        return variantValid && quantityValid && submitAvailable;
      }
    }
  );
}
